import { useCallback, useEffect, useRef, useState } from 'react';
import { readRequestError } from '../pages/runtimeLimitsForm';

/**
 * One GET a Fold page shows. `request` resolves to the payload. A late response never
 * overwrites a newer one, and a failed refresh keeps the last good payload on screen.
 */
export function useFoldResource(request, failureMessage) {
  const generation = useRef(0);
  const [resource, setResource] = useState({ status: 'loading' });

  const load = useCallback(async () => {
    const requestGeneration = ++generation.current;
    setResource((current) => (current.status === 'ready' ? { ...current, refreshing: true } : { status: 'loading' }));
    try {
      const payload = await request();
      if (requestGeneration === generation.current) {
        setResource({ status: 'ready', payload, loadedAt: new Date(), refreshing: false, refreshError: null });
      }
      return payload;
    } catch (error) {
      if (requestGeneration === generation.current) {
        const failure = readRequestError(error, failureMessage);
        const message =
          failure.type === 'no_response' ? 'The server did not respond. Check your connection.' : failure.message;
        setResource((current) => {
          if (failure.type === 'denied') return { status: 'denied' };
          if (current.status === 'ready') return { ...current, refreshing: false, refreshError: message };
          return { status: 'failed', message };
        });
      }
      return null;
    }
  }, [request, failureMessage]);

  useEffect(() => {
    load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  return [resource, load];
}
